import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { ImageUploadZone } from '../components/ImageUploadZone';
import { PLANBASE_CONFIG } from '../config';
import { decodeJWT } from '../utils/auth';
import { LoadingSpinner } from '../components/ui/spinner';
import { useAuth } from '../contexts/AuthContext';
import { Checkbox } from '../components/ui/checkbox';
import { MobilePageHeader } from '../components/MobilePageHeader';
import { resolveUserTier } from '../lib/plan';
import { useHideBottomNav } from '../hooks/useHideBottomNav';

import { ArrowPathIcon, CheckCircleIcon, XMarkIcon } from '@heroicons/react/24/outline';

interface ProjectImage {
  url: string;
  title: string;
  description: string;
}

interface ProjectFormData {
  title: string;
  description: string;
  category: string;
  style: string;
  location: string;
  year: string;
  architect: string;
  coverImage: string;
  images: ProjectImage[];
  tags: string[];
  allowPublishBeyondPlanbase?: boolean;
  isLocked?: boolean;
}

export default function EditProject() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  useHideBottomNav();
  const userPlan = resolveUserTier(user);
  const maxImages = userPlan === 'enterprise'
    ? PLANBASE_CONFIG.planLimits.enterprise.maxImagesPerProject
    : userPlan === 'pro'
      ? PLANBASE_CONFIG.planLimits.pro.maxImagesPerProject
      : PLANBASE_CONFIG.planLimits.free.maxImagesPerProject;

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [formData, setFormData] = useState<ProjectFormData>({
    title: '', description: '', category: '', style: '',
    location: '', year: '', architect: '', coverImage: '', images: [], tags: [],
  });
  const [newTag, setNewTag] = useState('');

  useEffect(() => {
    const fetchProject = async () => {
      try {
        const token = localStorage.getItem('planbase_token');
        if (!token) throw new Error('Please log in to edit projects');
        const payload = decodeJWT(token);
        const userId = payload?.sub || payload?.user_id || payload?.id;
        const response = await fetch(
          `${PLANBASE_CONFIG.apiUrl}/sdk/cms/records/${id}?database_slug=projects`,
          {
            headers: {
              'Authorization': `Bearer ${PLANBASE_CONFIG.apiKey}`,
              'X-Project-ID': PLANBASE_CONFIG.projectId,
              'X-User-ID': userId,
            },
          }
        );
        if (!response.ok) throw new Error(response.status === 404 ? 'Project not found' : 'Failed to load project');
        const data = await response.json();
        const projectData = data.record.data;
        if (projectData.created_by && projectData.created_by !== userId)
          throw new Error('You do not have permission to edit this project');
        setFormData(projectData);
      } catch (err: any) {
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };
    fetchProject();
  }, [id]);

  const handleSubmit = async (e: React.FormEvent, saveAsDraft = false) => {
    e.preventDefault();
    setSaving(true);
    setError(null);
    try {
      const token = localStorage.getItem('planbase_token');
      if (!token) throw new Error('Please log in to save changes');
      const payload = decodeJWT(token);
      const userId = payload?.sub || payload?.user_id || payload?.id;
      const response = await fetch(`${PLANBASE_CONFIG.apiUrl}/sdk/cms/records/${id}`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${PLANBASE_CONFIG.apiKey}`,
          'X-Project-ID': PLANBASE_CONFIG.projectId,
          'X-User-ID': userId,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ database_slug: 'projects', data: formData, status: saveAsDraft ? 'draft' : 'pending' }),
      });
      if (!response.ok) {
        const errorData = await response.json();
        if (response.status === 403) throw new Error('You do not have permission to edit this project');
        throw new Error(errorData.error || 'Failed to update project');
      }
      navigate('/portfolio');
    } catch (err: any) {
      setError(err.message);
    } finally {
      setSaving(false);
    }
  };

  const removeImage = (index: number) =>
    setFormData(prev => ({ ...prev, images: prev.images.filter((_, i) => i !== index) }));

  const updateImageDetails = (index: number, field: 'title' | 'description', value: string) =>
    setFormData(prev => ({
      ...prev,
      images: prev.images.map((img, i) => i === index ? { ...img, [field]: value } : img),
    }));

  const addTag = (value: string) => {
    const t = value.trim();
    if (t && !formData.tags.includes(t))
      setFormData(prev => ({ ...prev, tags: [...prev.tags, t] }));
  };

  const handleTagInput = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    if (value.includes(',')) {
      value.split(',').map(t => t.trim()).filter(Boolean).forEach(addTag);
      setNewTag('');
    } else {
      setNewTag(value);
    }
  };

  const handleTagKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter' && newTag.trim()) { e.preventDefault(); addTag(newTag); setNewTag(''); }
    else if (e.key === 'Backspace' && !newTag && formData.tags.length > 0)
      setFormData(prev => ({ ...prev, tags: prev.tags.slice(0, -1) }));
  };

  const set = (field: keyof ProjectFormData) => (val: any) =>
    setFormData(prev => ({ ...prev, [field]: val }));

  if (loading) return <LoadingSpinner message="Loading project..." className="py-20" />;

  return (
    <div className="max-w-3xl mx-auto space-y-4">
      <MobilePageHeader title="Edit Project" onBack={() => navigate('/portfolio')} />
      {/* Desktop header */}
      <div className="hidden md:flex items-center justify-between">
        <h1 className="text-sm font-semibold text-foreground uppercase tracking-wide">Edit Project</h1>
        <button type="button" onClick={() => navigate('/portfolio')}
          className="text-xs text-muted-foreground hover:text-foreground transition-colors">Cancel</button>
      </div>

      {error && (
        <div className="border border-destructive/40 bg-destructive/10 rounded-xl px-4 py-3">
          <p className="text-destructive text-sm">{error}</p>
        </div>
      )}

      <form onSubmit={handleSubmit}>
        <div className="bg-white dark:bg-card border border-border rounded-2xl overflow-hidden divide-y divide-border">

          {/* Title */}
          <div className="px-4 py-3 flex items-center gap-4">
            <span className="text-xs text-muted-foreground w-28 flex-shrink-0">Title *</span>
            <input
              type="text"
              required
              value={formData.title}
              onChange={e => set('title')(e.target.value)}
              className="flex-1 h-8 px-3 border border-input rounded-lg text-sm bg-background focus:outline-none focus:border-primary"
              placeholder="Modern Villa Design"
            />
          </div>

          {/* Description */}
          <div className="px-4 py-3 flex gap-4">
            <span className="text-xs text-muted-foreground w-28 flex-shrink-0 pt-2">Description *</span>
            <textarea
              required
              value={formData.description}
              onChange={e => set('description')(e.target.value)}
              rows={4}
              className="flex-1 px-3 py-2 border border-input rounded-lg text-sm bg-background focus:outline-none focus:border-primary resize-y min-h-[80px] max-h-[200px] scrollbar-hide"
              placeholder="Describe your project..."
            />
          </div>

          {/* Category + Style */}
          <div className="px-4 py-3 flex items-center gap-4">
            <span className="text-xs text-muted-foreground w-28 flex-shrink-0">Category *</span>
            <div className="flex-1 grid grid-cols-2 gap-3">
              <Select value={formData.category} onValueChange={set('category')}>
                <SelectTrigger><SelectValue placeholder="Category" /></SelectTrigger>
                <SelectContent>
                  {PLANBASE_CONFIG.categories.map(c => (
                    <SelectItem key={c.value} value={c.value}>{c.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <Select value={formData.style} onValueChange={set('style')}>
                <SelectTrigger><SelectValue placeholder="Style" /></SelectTrigger>
                <SelectContent>
                  {PLANBASE_CONFIG.styles.map(s => (
                    <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>

          {/* Location + Year + Architect */}
          <div className="px-4 py-3 flex items-center gap-4">
            <span className="text-xs text-muted-foreground w-28 flex-shrink-0">Details</span>
            <div className="flex-1 grid grid-cols-3 gap-3">
              <input
                type="text"
                value={formData.location}
                onChange={e => set('location')(e.target.value)}
                className="h-8 px-3 border border-input rounded-lg text-sm bg-background focus:outline-none focus:border-primary"
                placeholder="Location"
              />
              <input
                type="text"
                value={formData.year}
                onChange={e => set('year')(e.target.value)}
                className="h-8 px-3 border border-input rounded-lg text-sm bg-background focus:outline-none focus:border-primary"
                placeholder="Year"
              />
              <input
                type="text"
                value={formData.architect}
                onChange={e => set('architect')(e.target.value)}
                className="h-8 px-3 border border-input rounded-lg text-sm bg-background focus:outline-none focus:border-primary"
                placeholder="Architect"
              />
            </div>
          </div>

          {/* Tags */}
          <div className="px-4 py-3 flex gap-4">
            <span className="text-xs text-muted-foreground w-28 flex-shrink-0 pt-2">Tags</span>
            <div className="flex-1">
              <div className="border border-input rounded-lg px-3 py-2 bg-background focus-within:border-primary flex flex-wrap gap-1.5 items-center min-h-[32px]">
                {formData.tags.map((tag, i) => (
                  <span key={i} className="flex items-center gap-1 px-2 py-0.5 bg-muted rounded-full text-xs text-foreground">
                    {tag}
                    <button type="button" onClick={() => setFormData(prev => ({ ...prev, tags: prev.tags.filter((_, j) => j !== i) }))}>
                      <XMarkIcon className="h-2.5 w-2.5" />
                    </button>
                  </span>
                ))}
                <input
                  type="text"
                  value={newTag}
                  onChange={handleTagInput}
                  onKeyDown={handleTagKeyDown}
                  className="flex-1 min-w-[120px] text-sm bg-transparent focus:outline-none"
                  placeholder={formData.tags.length === 0 ? "Add tags..." : ""}
                />
              </div>
              <p className="text-[10px] text-muted-foreground mt-1">Enter or comma to add · Backspace to remove</p>
            </div>
          </div>

          {/* Cover Image */}
          <div className="px-4 py-3 flex gap-4">
            <span className="text-xs text-muted-foreground w-28 flex-shrink-0 pt-1">Cover Image *</span>
            <div className="flex-1">
              {formData.coverImage ? (
                <div className="relative rounded-xl overflow-hidden border border-border group w-full max-w-xs">
                  <img src={formData.coverImage} alt="Cover" className="w-full h-40 object-cover" />
                  <button
                    type="button"
                    onClick={() => set('coverImage')('')}
                    className="absolute top-2 right-2 p-1.5 bg-black/60 text-white rounded-full opacity-0 group-hover:opacity-100 transition-opacity"
                  >
                    <XMarkIcon className="h-3.5 w-3.5" />
                  </button>
                </div>
              ) : (
                <ImageUploadZone label="" required onImageUploaded={url => set('coverImage')(url)} />
              )}
            </div>
          </div>

          {/* Additional Images */}
          <div className="px-4 py-3 flex gap-4">
            <span className="text-xs text-muted-foreground w-28 flex-shrink-0 pt-1">
              Images
              <span className="block text-[10px] mt-0.5">{formData.images.length}/{maxImages}</span>
            </span>
            <div className="flex-1 space-y-3">
              {formData.images.length < maxImages && (
                <ImageUploadZone
                  label=""
                  maxFiles={maxImages}
                  currentCount={formData.images.length}
                  onImageUploaded={url => {
                    if (formData.images.length < maxImages)
                      setFormData(prev => ({ ...prev, images: [...prev.images, { url, title: '', description: '' }] }));
                  }}
                />
              )}
              {formData.images.map((image, index) => (
                <div key={index} className="flex gap-3 border border-border rounded-xl p-3">
                  <div className="relative group flex-shrink-0">
                    <img src={image.url} alt={image.title || `Image ${index + 1}`} className="w-20 h-20 object-cover rounded-lg" />
                    <button
                      type="button"
                      onClick={() => removeImage(index)}
                      className="absolute -top-1.5 -right-1.5 p-1 bg-black/60 text-white rounded-full opacity-0 group-hover:opacity-100 transition-opacity"
                    >
                      <XMarkIcon className="h-2.5 w-2.5" />
                    </button>
                  </div>
                  <div className="flex-1 space-y-2">
                    <input
                      type="text"
                      value={image.title}
                      onChange={e => updateImageDetails(index, 'title', e.target.value)}
                      className="w-full h-8 px-3 border border-input rounded-lg text-sm bg-background focus:outline-none focus:border-primary"
                      placeholder="Image title"
                    />
                    <textarea
                      value={image.description}
                      onChange={e => updateImageDetails(index, 'description', e.target.value)}
                      rows={2}
                      className="w-full px-3 py-1.5 border border-input rounded-lg text-sm bg-background focus:outline-none focus:border-primary resize-none"
                      placeholder="Brief description"
                    />
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Permissions */}
          <div className="px-4 py-3 space-y-3">
            <Checkbox
              checked={formData.allowPublishBeyondPlanbase || false}
              onChange={v => set('allowPublishBeyondPlanbase')(v)}
            >
              Allow Planbase to publish my project beyond the platform (social media, newsletters, partner sites)
            </Checkbox>
            <Checkbox
              checked={formData.isLocked || false}
              onChange={v => set('isLocked')(v)}
            >
              Lock this project — hide from discovery. Only people with your direct link can view it.
            </Checkbox>
          </div>

          {/* Footer actions */}
          <div className="px-4 py-3 bg-muted/40 flex items-center justify-end gap-2">
            <button
              type="button"
              onClick={(e: any) => handleSubmit(e, true)}
              disabled={saving}
              className="h-8 px-4 rounded-lg border border-border text-xs font-medium text-foreground hover:bg-background transition-colors disabled:opacity-50"
            >
              Save as Draft
            </button>
            <button
              type="submit"
              disabled={saving}
              className="h-8 px-4 rounded-lg bg-primary text-white text-xs font-medium hover:bg-primary/90 transition-colors disabled:opacity-50 flex items-center gap-1.5"
            >
              {saving ? (
                <><ArrowPathIcon className="h-3.5 w-3.5 animate-spin" /> Saving...</>
              ) : (
                <><CheckCircleIcon className="h-3.5 w-3.5" /> Save Changes</>
              )}
            </button>
          </div>
        </div>
      </form>
    </div>
  );
}
